import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Stack,
  Link,
  CircularProgress,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import DiamondIcon from "@mui/icons-material/Diamond";
import PaletteIcon from "@mui/icons-material/Palette";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GavelIcon from "@mui/icons-material/Gavel";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useNavigate } from "react-router-dom";
import { useAssetCategoryQuery } from "../../../redux/api/authApi";

const TOTAL_STEPS = 8;

/** When API icon URL fails or is absent, pick an MUI icon from category name. */
function getFallbackIconComponent(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("real estate")) return ApartmentIcon;
  if (n.includes("renewable")) return EnergySavingsLeafIcon;
  if (n.includes("commodit")) return DiamondIcon;
  if (n.includes("arts") || n.includes("collectible")) return PaletteIcon;
  if (n.includes("luxury")) return DiamondOutlinedIcon;
  if (n.includes("supply chain") || n.includes("inventory")) return LocalShippingIcon;
  if (n.includes("equipment") || n.includes("machinery")) return PrecisionManufacturingIcon;
  if (n.includes("debt")) return AccountBalanceIcon;
  if (n.includes("islamic")) return GavelIcon;
  return CategoryOutlinedIcon;
}

/**
 * Build URL for API-provided icon filename. Adjust REACT_APP_CATEGORY_ICON_BASE if your CDN path differs.
 */
function getCategoryIconSrc(iconFilename) {
  if (!iconFilename || typeof iconFilename !== "string") return null;
  const base = (process.env.REACT_APP_ASSET_OWNER_Url || "").replace(/\/$/, "");
  const customBase = process.env.REACT_APP_CATEGORY_ICON_BASE;
  const folder = customBase != null && customBase !== ""
    ? String(customBase).replace(/\/$/, "")
    : `${base}/uploads/categories`;
  return `${folder}/${iconFilename.replace(/^\//, "")}`;
}

function CategoryGlyph({ name, iconFilename }) {
  const [imgFailed, setImgFailed] = useState(false);
  const Fallback = useMemo(() => getFallbackIconComponent(name), [name]);
  const src = getCategoryIconSrc(iconFilename);

  if (!src || imgFailed) {
    return <Fallback sx={{ fontSize: 26 }} />;
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setImgFailed(true)}
      sx={{ width: 26, height: 26, objectFit: "contain" }}
    />
  );
}

export default function ListNewAssetStep1({ nextStep }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(false);

  const { data: apiResponse, isLoading, isError, refetch } = useAssetCategoryQuery();

  const categories = useMemo(() => {
    const raw = apiResponse?.data;
    if (!Array.isArray(raw)) return [];
    return raw.map((c) => ({
      id: c._id,
      title: c.name,
      subtitle: c.description || "",
      examples: Array.isArray(c.examples) ? c.examples.join(", ") : String(c.examples || ""),
      icon: c.icon,
      raw: c,
    }));
  }, [apiResponse]);

  const handleNext = () => {
    if (!selectedId) {
      setError(true);
      return;
    }
    setError(false);
    const selected = categories.find((item) => item.id === selectedId);
    if (!selected) {
      setError(true);
      return;
    }
    nextStep({
      id: selected.id,
      title: selected.title,
      description: selected.subtitle,
      examples: selected.examples,
      icon: selected.icon,
    });
  };

  const exitFlow = () => navigate("/asset/listings");

  // const selectedCat = categories.find((item) => item.id === selectedId);
  // const displayTotalSteps =
  //   !selectedId || !selectedCat
  //     ? 8
  //     : String(selectedCat.title || "").trim() === "Real Estate"
  //       ? 8
  //       : 7;

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1100, mx: "auto" }}>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={exitFlow}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "text.neutral",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: "text.primary" },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
          Back
        </Link>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
          Step 1 of {TOTAL_STEPS}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              bgcolor: i === 0 ? TEAL : theme.palette.border.light,
            }}
          />
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: "14px",
          bgcolor: "background.paper",
          boxShadow: (theme) => theme.palette.shadow.cardStrong,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}
        >
          Select Asset Category
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
          Choose the type of asset you want to tokenize
        </Typography>

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress sx={{ color: TEAL }} />
            <Typography sx={{ mt: 2, fontSize: 14, color: "text.muted" }}>
              Loading categories…
            </Typography>
          </Stack>
        )}

        {isError && !isLoading && (
          <Stack alignItems="flex-start" spacing={2} sx={{ py: 2 }}>
            <Typography sx={{ color: "error.main", fontSize: 14 }}>
              Could not load categories. Please try again.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => refetch()}
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
          </Stack>
        )}

        {!isLoading && !isError && categories.length === 0 && (
          <Typography sx={{ color: "text.muted", fontSize: 14, py: 2 }}>
            No categories available.
          </Typography>
        )}

        {!isLoading && !isError && categories.length > 0 && (
          <Grid container spacing={2}>
            {categories.map((item) => {
              const isSelected = selectedId === item.id;
              return (
                <Grid size={{ xs: 12, sm: 6 }} key={item.id}>
                  <Paper
                    onClick={() => {
                      setSelectedId(item.id);
                      setError(false);
                    }}
                    elevation={0}
                    sx={{
                      p: 2.25,
                      borderRadius: "12px",
                      cursor: "pointer",
                      border: `2px solid ${
                        isSelected ? TEAL : theme.palette.border.light
                      }`,
                      bgcolor: "background.paper",
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: (theme) => theme.palette.shadow.cardHover,
                        borderColor: TEAL,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isSelected
                            ? theme.palette.brand.cyanSoft
                            : theme.palette.background.default,
                          color: isSelected
                            ? theme.palette.brand.cyan
                            : theme.palette.text.neutral,
                          transition: "background-color 0.2s, color 0.2s",
                          flexShrink: 0,
                        }}
                      >
                        <CategoryGlyph name={item.title} iconFilename={item.icon} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: "text.primary",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13, color: "text.muted", mt: 0.25 }}
                        >
                          {item.subtitle}
                        </Typography>
                        {item.examples ? (
                          <Typography
                            sx={{ fontSize: 12, color: "text.faint", mt: 0.5 }}
                          >
                            Examples: {item.examples}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        {error && (
          <Typography sx={{ color: "error.main", fontSize: 14, mt: 2 }}>
            Please select a category before continuing.
          </Typography>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 1 }}
        >
          <Button
            variant="text"
            sx={{
              color: "text.neutral",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 1,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
            }}
            onClick={exitFlow}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isLoading || isError || categories.length === 0}
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
              px: 1.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: "12px",
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
            onClick={handleNext}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
